import { useCallback, useMemo, type ReactNode } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import config from 'constants/config'
import { INTL_DAY_MONTH_NAME_SHORT, INTL_DAY_ONLY } from 'constants/dateFormats'
import HeightFillingImage from 'components/Common/HeightFillingImage'
import LayoutContainer from 'components/Common/LayoutContainer'
import Sheet from 'components/utils/Sheet'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import TextLink from 'LuxKit/TextLink'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import UrgencyLabel from 'LuxKit/Label/UrgencyLabel'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import useOffer from 'hooks/Offers/useOffer'
import useOrder from 'hooks/Order/useOrder'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { getDaysToGoLabel } from 'checkout/Components/Confirmation/BookingDetailsV2/utils'
import UpcomingEscapeUpsellList from './UpcomingEscapeUpsellList'
import useUpcomingEscapeUpsells from './useUpcomingEscapeUpsells'
import getUpcomingEscapeBookingTitle from './getUpcomingEscapeBookingTitle'
import getUpcomingEscapePrimaryBooking from './getUpcomingEscapePrimaryBooking'

const Section = styled(LayoutContainer)`
  padding-top: ${rem(40)};
`

const Card = styled(Sheet)`
  display: flex;
  flex-direction: column;
  overflow: hidden;

  ${mediaQueryUp.tablet} {
    flex-direction: row;
    align-items: stretch;
    gap: ${rem(24)};
  }
`

const ImageContainer = styled.div`
  width: 100%;
  height: ${rem(160)};
  flex-shrink: 0;
  overflow: hidden;

  ${mediaQueryUp.tablet} {
    width: ${rem(220)};
    height: auto;
    align-self: stretch;
    border-radius: ${props => props.theme.borderRadius.L};
  }
`

const Content = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${rem(20)};
  padding: ${rem(16)};

  ${mediaQueryUp.tablet} {
    padding: 0;
  }
`

interface Props {
  order: App.Order
}

function UpcomingEscapeCard({ order }: Props) {
  const intl = useIntl()
  const dateFormatter = useIntlDateFormatter()

  // Full-order fetch so lounge/eSIM redemption state is available.
  const [fullOrder] = useOrder(order.id)
  const resolvedOrder = fullOrder ?? order

  const primaryBooking = getUpcomingEscapePrimaryBooking(resolvedOrder)
  const stayOfferId = primaryBooking.kind === 'stay' ? primaryBooking.stayOfferId : undefined
  const [offer] = useOffer<App.Offer | App.BedbankOffer>(stayOfferId)
  const checkInDate = primaryBooking.startDate
  const checkOutDate = primaryBooking.endDate

  const formatFlightRoute = useCallback((departureAirport: string, arrivalAirport: string) => (
    intl.formatMessage(
      {
        id: '1ywXJcItUQtqDs9eHBqt',
        defaultMessage: '{departureAirport} to {arrivalAirport}',
        description: 'Standalone flight route title on the homepage Upcoming Escape card when only airport codes are available',
      },
      { departureAirport, arrivalAirport },
    )
  ), [intl])

  const bookingTitle = getUpcomingEscapeBookingTitle(resolvedOrder, offer, formatFlightRoute)
  const imageId = (primaryBooking.kind === 'stay' ? offer?.image?.id : undefined)
    || primaryBooking.imageId

  const dateLabel = useMemo(() => {
    if (!checkInDate || !checkOutDate) return ''
    const startDate = dateFormatter(checkInDate, INTL_DAY_ONLY)
    const endDate = dateFormatter(checkOutDate, INTL_DAY_MONTH_NAME_SHORT)
    if (!startDate || !endDate) return ''

    return intl.formatMessage(
      {
        id: 'R3Xba2_yeMYjJ9kQ1fba',
        defaultMessage: '{startDate} - {endDate}',
        description: 'Short stay date range on the homepage Upcoming Escape card',
      },
      { startDate, endDate },
    )
  }, [checkInDate, checkOutDate, dateFormatter, intl])

  const daysToGoLabel = getDaysToGoLabel(
    intl,
    primaryBooking.startDate,
    primaryBooking.duration,
    primaryBooking.kind,
  )
  const upsells = useUpcomingEscapeUpsells(resolvedOrder)
  const {
    eligibility: {
      showFlight,
      showCarHire,
      showTravelProtection,
      showCancellationProtection,
      showTour,
      showAirportTransfer,
      showLounge,
      showEsim,
    },
  } = upsells

  const hasVisibleRows = showFlight
    || showCarHire
    || showTravelProtection
    || showCancellationProtection
    || showTour
    || showAirportTransfer
    || showLounge
    || showEsim

  const manageBookingLink = useCallback((chunks: ReactNode) => (
    <TextLink
      key="manage-booking"
      to={config.agentHub.isEnabled ? undefined : `/account/my-escapes/order/${resolvedOrder.id}`}
      href={config.agentHub.isEnabled ? `${config.AGENT_PORTAL_URL}/bookings` : undefined}
      target={config.agentHub.isEnabled ? '_blank' : undefined}
      weight="regular"
      data-testid="manage-booking"
    >
      {chunks}
    </TextLink>
  ), [resolvedOrder.id])

  const bookingMeta = dateLabel
    ? intl.formatMessage(
        {
          id: 'cRAD2BmT4RNK7UAN7byo',
          defaultMessage: '{dateLabel} · <link>Manage booking</link>',
          description: 'Date label with manage booking link on the homepage Upcoming Escape card',
        },
        {
          dateLabel,
          link: manageBookingLink,
        },
      )
    : intl.formatMessage(
        {
          id: 'osNsihazW8PZ1ELQ2Hh0',
          defaultMessage: '<link>Manage booking</link>',
          description: 'Manage booking link on Upcoming Escape when stay dates are unavailable',
        },
        {
          link: manageBookingLink,
        },
      )

  // No booking title and no eligible rows → nothing to show (avoid empty HomepageSection padding).
  if (!bookingTitle && !hasVisibleRows) {
    return null
  }

  return (
    <Section data-testid="upcoming-escape-section">
      <Card border="light-foreground" borderRadius="L" padding={0} tabletPadding={24} shadow="flat-small">
        {imageId && (
          <ImageContainer>
            <HeightFillingImage fit="center" id={imageId} />
          </ImageContainer>
        )}
        <Content>
          <Group direction="vertical" gap={8}>
            {daysToGoLabel && (
              <LabelGroup>
                <UrgencyLabel variant="speed">{daysToGoLabel}</UrgencyLabel>
              </LabelGroup>
            )}
            {bookingTitle && <Heading variant="heading3">{bookingTitle}</Heading>}
            <BodyText variant="medium">{bookingMeta}</BodyText>
          </Group>
          <UpcomingEscapeUpsellList order={resolvedOrder} upsells={upsells} />
        </Content>
      </Card>
    </Section>
  )
}

export default UpcomingEscapeCard
